/**
 * Entity lifecycle event bus (AC-1369).
 *
 * Same-tab pub/sub for "entity X was deleted/updated/created" notifications.
 * Components that mutate an entity emit; components that display it subscribe.
 * This is the invalidation channel that's been missing - without it, every
 * component that fetches an entity caches the result in local state with no
 * way to know the entity changed elsewhere.
 *
 * Scope deliberately kept tight:
 *   - Same tab only. A BroadcastChannel layer can be added on top later if
 *     cross-tab invalidation matters; cross-user invalidation belongs on the
 *     backend SSE pipeline.
 *   - In-memory. No persistence. Subscribers that mount after an event
 *     fires don't see it - that's by design; mount-time fetch covers them.
 *   - Type-safe entity name field so adding a new entity is a one-line change.
 */

export type EntityType = 'queue' | 'agent' | 'model' | 'workflow' | 'schedule' | 'project';

export type EntityChangeKind = 'deleted' | 'updated' | 'created';

export interface EntityEvent {
  entity: EntityType;
  kind: EntityChangeKind;
  id: number | string;
}

type Listener = (event: EntityEvent) => void;

const listeners = new Set<Listener>();

/**
 * Emit an entity lifecycle event. Call this immediately AFTER a successful
 * mutation request - emitting before the request returns risks notifying
 * subscribers of a change that the server then rejects.
 */
export function emitEntityEvent(event: EntityEvent): void {
  // Iterate over a snapshot so a listener that unsubscribes during dispatch
  // doesn't mutate the iteration set out from under us.
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch (err) {
      console.error('entityEvents listener threw:', err);
    }
  }
}

/**
 * Subscribe to all entity events. Returns an unsubscribe function so callers
 * can wire it directly to a useEffect cleanup. Most consumers should use the
 * useEntityEvent React hook instead, which handles the lifecycle.
 */
export function onEntityEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: clears all listeners. Don't call this in app code. */
export function _clearListenersForTesting(): void {
  listeners.clear();
}
